import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import type { User } from '@/types';
import { setAccessToken, clearTokens } from '@/services/api';
import authApi from '@/services/auth';
import settingsApi from '@/services/settings';
import { broadcastLogout } from '@/utils/broadcastChannel';
import { decodeTokenClaims, type TrialClaims } from '@/utils/tokenClaims';

/**
 * WP-AUTH-15: Extract trial and entitlement claims from JWT token.
 * Called after login/refresh to populate token-derived state.
 */
function extractClaimsFromToken(accessToken: string): {
  trialStatus: TrialClaims | null;
  entitlements: string[];
} {
  try {
    const claims = decodeTokenClaims(accessToken);
    return {
      trialStatus: claims.trial ?? null,
      entitlements: claims.entitlements ?? [],
    };
  } catch {
    // Token decode failed - return empty state
    return { trialStatus: null, entitlements: [] };
  }
}

/**
 * Auth state stored in localStorage (non-sensitive data only)
 * Note: Tokens are NOT stored here - access token is in memory,
 * refresh token is in httpOnly cookie
 */
interface PersistedAuthState {
  // Non-sensitive user info for UI purposes
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
}

/**
 * Login result type
 */
export type LoginResult =
  | { requiresMfa?: false; mfaToken?: undefined }
  | { requiresMfa: true; mfaToken: string };

interface AuthState extends PersistedAuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mfaRequired: boolean;
  mfaToken: string | null;
  // WP-AUTH-15: Token-derived state (computed at login/refresh)
  trialStatus: TrialClaims | null;
  entitlements: string[];

  // Actions
  login: (email: string, password: string, deviceFingerprint?: string, deviceName?: string) => Promise<LoginResult>;
  loginWithGoogle: (idToken: string, termsAccepted?: boolean, deviceFingerprint?: string) => Promise<LoginResult>;
  /**
   * Login with Apple OAuth (Sign in with Apple)
   * WP-FE-AUTH-1: Apple Sign In Web Frontend Integration
   *
   * @param idToken - The Apple ID token from Sign in with Apple
   * @param termsAccepted - Optional boolean indicating terms acceptance (required for new user registration)
   * @param deviceFingerprint - Optional device fingerprint for MFA device trust
   * @param userFirstName - User's first name (only provided by Apple on first authorization)
   * @param userLastName - User's last name (only provided by Apple on first authorization)
   */
  loginWithApple: (
    idToken: string,
    termsAccepted?: boolean,
    deviceFingerprint?: string,
    userFirstName?: string,
    userLastName?: string
  ) => Promise<LoginResult>;
  /** WP-SEC-27: no password at sign-up; it is set after the email is verified. */
  register: (email: string, name: string, gdprConsent: boolean, marketingConsent?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  verifyMfa: (mfaToken: string, code: string, mfaMethod?: 'totp' | 'sms' | 'recovery' | 'auto', deviceFingerprint?: string, trustDevice?: boolean) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string, deviceFingerprint?: string) => Promise<LoginResult>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  clearMfaState: () => void;
}

/**
 * Auth store using Zustand with persistence for non-sensitive data
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      // WP-AUTH-12: Start with isLoading: true to prevent flash redirect.
      //
      // AuthProvider NO LONGER gates the app on this flag. It is shared by
      // every auth action - login, register, verifyMfa, magic link - so gating
      // on it meant pressing "Sign in" unmounted the login form and blanked the
      // app behind "Checking authentication...". AuthProvider now tracks
      // whether the INITIAL checkAuth() has settled, which is what that gate
      // always meant; this flag remains for components that want to know an
      // auth request is in flight.
      user: null,
      userId: null,
      userEmail: null,
      userName: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      mfaRequired: false,
      mfaToken: null,
      // WP-AUTH-15: Token-derived state
      trialStatus: null,
      entitlements: [],

      /**
       * Login with email/password
       * Returns MFA info if MFA is required
       * @param deviceFingerprint - Optional device fingerprint for device tracking
       * @param deviceName - Optional device name derived from user agent
       */
      login: async (email: string, password: string, deviceFingerprint?: string, deviceName?: string): Promise<LoginResult> => {
        set({ isLoading: true, error: null, mfaRequired: false, mfaToken: null });

        try {
          const response = await authApi.login({
            email,
            password,
            deviceFingerprint,
            deviceName,
          });

          if (response.requiresMfa && response.mfaToken) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaToken: response.mfaToken
            });
            return { requiresMfa: true, mfaToken: response.mfaToken };
          }

          // WP-AUTH-12: Only store access token in memory
          // Refresh token is managed via httpOnly cookie by the backend
          setAccessToken(response.tokens.accessToken);

          // WP-AUTH-15: Extract claims from token
          const { trialStatus, entitlements } = extractClaimsFromToken(response.tokens.accessToken);

          // User should always be present when MFA is not required
          const user = response.user!;

          set({
            user: user,
            userId: user.id,
            userEmail: user.email,
            userName: user.name,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaToken: null,
            trialStatus,
            entitlements,
          });

          // Wait for React 18 batched state updates to flush to all subscribers
          // This prevents race conditions where navigation happens before state settles
          await new Promise<void>(resolve => queueMicrotask(() => resolve()));

          return {};
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed. Please check your credentials.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Login with Google OAuth
       * Sends the Google ID token to backend for validation
       * Returns MFA info if MFA is required
       * @param idToken - The Google ID token from Google Sign-In
       * @param termsAccepted - Optional boolean indicating terms acceptance (required for new user registration)
       * @param deviceFingerprint - Optional device fingerprint for MFA device trust (WP-AUTH-7)
       */
      loginWithGoogle: async (idToken: string, termsAccepted?: boolean, deviceFingerprint?: string): Promise<LoginResult> => {
        // WP-BUG-61: Don't set isLoading:true — it causes AuthProvider to unmount the App tree,
        // creating a timing window where ProtectedRoute redirects back to /login.
        // SocialLoginButtons manages its own local loading state for UI feedback.
        set({ error: null, mfaRequired: false, mfaToken: null });

        try {
          const response = await authApi.loginWithGoogle(idToken, termsAccepted, deviceFingerprint);

          // WP-AUTH-7: Check if MFA is required BEFORE accessing user
          if (response.requiresMfa && response.mfaToken) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaToken: response.mfaToken,
            });
            return { requiresMfa: true, mfaToken: response.mfaToken };
          }

          // MFA not required - proceed with login
          // WP-AUTH-12: Only store access token in memory
          // Refresh token is managed via httpOnly cookie by the backend
          setAccessToken(response.tokens.accessToken);

          // WP-AUTH-15: Extract claims from token
          const { trialStatus, entitlements } = extractClaimsFromToken(response.tokens.accessToken);

          // User is guaranteed to exist when MFA is not required
          const user = response.user!;

          // PII minimization: Default new OAuth users to "User 1" for privacy
          // Users can change this in settings - this prevents exposing their real name
          let displayName = user.name;
          if (response.isNewUser) {
            try {
              await settingsApi.updateProfile({ name: 'User 1' });
              displayName = 'User 1';
            } catch {
              // Non-critical - user can change in settings
            }
          }

          set({
            user,
            userId: user.id,
            userEmail: user.email,
            userName: displayName,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaToken: null,
            trialStatus,
            entitlements,
          });

          // Wait for React 18 batched state updates to flush to all subscribers
          // This prevents race conditions where navigation happens before state settles
          await new Promise<void>(resolve => queueMicrotask(() => resolve()));

          return {};
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google sign-in failed. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Login with Apple OAuth (Sign in with Apple)
       * WP-FE-AUTH-1: Apple Sign In Web Frontend Integration
       *
       * Sends the Apple ID token to backend for validation
       * Returns MFA info if MFA is required
       *
       * @param idToken - The Apple ID token from Sign in with Apple
       * @param termsAccepted - Optional boolean indicating terms acceptance (required for new user registration)
       * @param deviceFingerprint - Optional device fingerprint for MFA device trust (WP-AUTH-7)
       * @param userFirstName - User's first name (only provided by Apple on first authorization)
       * @param userLastName - User's last name (only provided by Apple on first authorization)
       */
      loginWithApple: async (
        idToken: string,
        termsAccepted?: boolean,
        deviceFingerprint?: string,
        userFirstName?: string,
        userLastName?: string
      ): Promise<LoginResult> => {
        // WP-BUG-61: Don't set isLoading:true — same fix as loginWithGoogle above.
        set({ error: null, mfaRequired: false, mfaToken: null });

        try {
          const response = await authApi.loginWithApple(
            idToken,
            termsAccepted,
            deviceFingerprint,
            userFirstName,
            userLastName
          );

          // WP-AUTH-7: Check if MFA is required BEFORE accessing user
          if (response.requiresMfa && response.mfaToken) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaToken: response.mfaToken,
            });
            return { requiresMfa: true, mfaToken: response.mfaToken };
          }

          // MFA not required - proceed with login
          // WP-AUTH-12: Only store access token in memory
          // Refresh token is managed via httpOnly cookie by the backend
          setAccessToken(response.tokens.accessToken);

          // WP-AUTH-15: Extract claims from token
          const { trialStatus, entitlements } = extractClaimsFromToken(response.tokens.accessToken);

          // User is guaranteed to exist when MFA is not required
          const user = response.user!;

          // PII minimization: Default new OAuth users to "User 1" for privacy
          // Users can change this in settings - this prevents exposing their real name
          let displayName = user.name;
          if (response.isNewUser) {
            try {
              await settingsApi.updateProfile({ name: 'User 1' });
              displayName = 'User 1';
            } catch {
              // Non-critical - user can change in settings
            }
          }

          set({
            user,
            userId: user.id,
            userEmail: user.email,
            userName: displayName,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaToken: null,
            trialStatus,
            entitlements,
          });

          // Wait for React 18 batched state updates to flush to all subscribers
          // This prevents race conditions where navigation happens before state settles
          await new Promise<void>(resolve => queueMicrotask(() => resolve()));

          return {};
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Apple sign-in failed. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Register new user
       * After registration, user needs to verify email
       *
       * WP-SEC-27: registration creates no session and sets no password. The
       * verification link proves the address and leads to "set your password".
       *
       * Note: Unlike login/checkAuth, register does NOT set isLoading: true
       * because that would trigger AuthProvider to unmount the SignupWizard,
       * losing the registration success state. The wizard handles its own
       * loading state via isSubmitting in WizardContext.
       */
      register: async (email: string, name: string, gdprConsent: boolean, marketingConsent?: boolean) => {
        set({ error: null });

        try {
          // Registration returns user info but user is not authenticated until email is verified
          await authApi.register({ email, name, gdprConsent, marketingConsent });
          // Success - no state change needed (wizard shows success screen)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed. Please try again.';
          set({ error: message });
          throw error;
        }
      },

      /**
       * Logout user
       * WP-AUTH-12: Clears local state and broadcasts logout to other tabs
       */
      logout: async () => {
        set({ isLoading: true });

        try {
          // WP-BUG-60: Revoke ALL sessions so other tabs can't remain authenticated
          await authApi.logout({ allSessions: true });
        } catch {
          // Ignore logout errors, still clear local state
        } finally {
          // WP-AUTH-12: Clear access token from memory
          // Refresh token cookie is cleared by the backend
          clearTokens();
          set({
            user: null,
            userId: null,
            userEmail: null,
            userName: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            mfaRequired: false,
            mfaToken: null,
            // WP-AUTH-15: Clear token-derived state
            trialStatus: null,
            entitlements: [],
          });

          // WP-AUTH-12: Notify other tabs to logout
          broadcastLogout();
        }
      },

      /**
       * Verify MFA code
       * @param deviceFingerprint - Optional device fingerprint for device tracking
       * @param trustDevice - Whether to trust this device for future logins
       */
      verifyMfa: async (mfaToken: string, code: string, mfaMethod: 'totp' | 'sms' | 'recovery' | 'auto' = 'auto', deviceFingerprint?: string, trustDevice?: boolean) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.verifyMfa(mfaToken, code, mfaMethod, deviceFingerprint, trustDevice);

          // WP-AUTH-12: Only store access token in memory
          // Refresh token is managed via httpOnly cookie by the backend
          setAccessToken(response.tokens.accessToken);

          // WP-AUTH-15: Extract claims from token
          const { trialStatus, entitlements } = extractClaimsFromToken(response.tokens.accessToken);

          // User is always present after successful MFA verification
          const user = response.user!;

          set({
            user: user,
            userId: user.id,
            userEmail: user.email,
            userName: user.name,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaToken: null,
            trialStatus,
            entitlements,
          });

          // Wait for React 18 batched state updates to flush to all subscribers
          // This prevents race conditions where navigation happens before state settles
          await new Promise<void>(resolve => queueMicrotask(() => resolve()));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Invalid verification code. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Request magic link email
       */
      requestMagicLink: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          await authApi.requestMagicLink(email);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to send magic link. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Verify magic link token
       * Returns MFA info if MFA is required
       * @param token - Magic link token from email
       * @param deviceFingerprint - Optional device fingerprint for device tracking
       */
      verifyMagicLink: async (token: string, deviceFingerprint?: string): Promise<LoginResult> => {
        set({ isLoading: true, error: null, mfaRequired: false, mfaToken: null });

        try {
          const response = await authApi.verifyMagicLink(token, deviceFingerprint);

          // WP-AUTH-7: Check if MFA is required BEFORE accessing user
          if (response.requiresMfa && response.mfaToken) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaToken: response.mfaToken,
            });
            return { requiresMfa: true, mfaToken: response.mfaToken };
          }

          // MFA not required - proceed with login
          // WP-AUTH-12: Only store access token in memory
          // Refresh token is managed via httpOnly cookie by the backend
          setAccessToken(response.tokens.accessToken);

          // WP-AUTH-15: Extract claims from token
          const { trialStatus, entitlements } = extractClaimsFromToken(response.tokens.accessToken);

          // User is guaranteed to exist when MFA is not required
          const user = response.user!;

          set({
            user: user,
            userId: user.id,
            userEmail: user.email,
            userName: user.name,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaToken: null,
            trialStatus,
            entitlements,
          });

          // Wait for React 18 batched state updates to flush to all subscribers
          // This prevents race conditions where navigation happens before state settles
          await new Promise<void>(resolve => queueMicrotask(() => resolve()));

          return {};
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Invalid or expired magic link.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Request password reset email
       */
      requestPasswordReset: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          await authApi.forgotPassword(email);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to send reset email. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Reset password with token
       */
      resetPassword: async (token: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          await authApi.resetPassword(token, password);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Check if user is authenticated (on app load)
       * WP-AUTH-12: Always attempts refresh - httpOnly cookie is the persistence mechanism
       * Don't check for persisted userId - the cookie IS our session persistence
       */
      checkAuth: async () => {
        // WP-BUG-61: Skip if already authenticated (e.g., OAuth login completed
        // while a previous checkAuth was still in-flight). Without this guard,
        // a stale checkAuth failure can overwrite isAuthenticated:true set by
        // loginWithGoogle/loginWithApple.
        if (get().isAuthenticated) return;

        set({ isLoading: true });

        try {
          // WP-AUTH-12: Always attempt refresh - browser sends cookie automatically
          // Don't check for persisted userId - cookie is the source of truth
          const tokens = await authApi.refresh();
          // WP-AUTH-12: Only store access token in memory
          setAccessToken(tokens.accessToken);

          // WP-AUTH-15: Extract claims from refreshed token
          const { trialStatus, entitlements } = extractClaimsFromToken(tokens.accessToken);

          // Get user info
          const user = await authApi.me();

          set({
            user,
            userId: user.id,
            userEmail: user.email,
            userName: user.name,
            isAuthenticated: true,
            isLoading: false,
            trialStatus,
            entitlements,
          });
        } catch (error) {
          // WP-BUG-51: Distinguish "session truly invalid" from "temporary failure"
          //
          // Errors from the refresh path are raw AxiosErrors (rawRefreshClient has
          // no interceptors). The coordinator may also throw a plain Error on timeout.
          const isRateLimit = isAxiosError(error) && error.response?.status === 429;
          const isNetworkError = isAxiosError(error) && !error.response;

          if (isRateLimit || isNetworkError) {
            // Temporary failure - don't log user out
            // Keep current auth state, just stop loading
            console.debug(
              '[AuthStore] Temporary refresh failure, keeping auth state:',
              isRateLimit ? '429 rate limited' : 'network error'
            );
            set({ isLoading: false });
            return;
          }

          // WP-BUG-61: Check if user was authenticated by another flow (OAuth)
          // while this checkAuth was in-flight. If so, don't clear their session.
          // Race condition: AuthProvider fires checkAuth() on mount (fire-and-forget).
          // If user completes OAuth login before checkAuth's refresh resolves,
          // the 401 from the stale refresh must NOT overwrite isAuthenticated:true.
          if (get().isAuthenticated) {
            console.debug('[AuthStore] checkAuth failed but user authenticated by another flow, keeping auth state');
            set({ isLoading: false });
            return;
          }

          // Session truly invalid (401, 403, etc.) - clear auth
          clearTokens();
          set({
            user: null,
            userId: null,
            userEmail: null,
            userName: null,
            isAuthenticated: false,
            isLoading: false,
            trialStatus: null,
            entitlements: [],
          });
        }
      },

      /**
       * Clear error state
       */
      clearError: () => set({ error: null }),

      /**
       * Clear MFA state
       */
      clearMfaState: () => set({ mfaRequired: false, mfaToken: null }),

      /**
       * Set user (for external updates)
       */
      setUser: (user: User | null) => {
        if (user) {
          set({
            user,
            userId: user.id,
            userEmail: user.email,
            userName: user.name,
            isAuthenticated: true,
          });
        } else {
          set({
            user: null,
            userId: null,
            userEmail: null,
            userName: null,
            isAuthenticated: false,
          });
        }
      },
    }),
    {
      name: 'flowio-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist non-sensitive data
      partialize: (state) => ({
        userId: state.userId,
        userEmail: state.userEmail,
        userName: state.userName,
      }),
    }
  )
);

export default useAuthStore;
